import {useCallback, useMemo} from "react";

type Key = string;

type UseCheckboxTableSelectionParams<T, TKey extends Key> = {
  rows: T[];
  selectedRowKeys: TKey[];
  getRowKey: (row: T) => TKey;
  onChangeSelectedRowKeys: (keys: TKey[]) => void;
};

export const useTableSelection = <T, TKey extends Key>({
  rows,
  selectedRowKeys,
  getRowKey,
  onChangeSelectedRowKeys,
}: UseCheckboxTableSelectionParams<T, TKey>) => {
  const selectedKeySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);
  const allRowKeys = useMemo(() => rows.map(getRowKey), [getRowKey, rows]);
  const isAllSelected = rows.length > 0 && selectedRowKeys.length === rows.length;
  const isPartiallySelected = selectedRowKeys.length > 0 && selectedRowKeys.length < rows.length;

  const handleChangeRow = useCallback((keys: TKey[]) => {
    onChangeSelectedRowKeys(keys);
  }, [onChangeSelectedRowKeys]);

  const handleToggleAll = useCallback((checked: boolean) => {
    handleChangeRow(checked ? allRowKeys : []);
  }, [allRowKeys, handleChangeRow]);

  const toggleRow = useCallback((row: T, checked: boolean) => {
    const rowKey = getRowKey(row);
    const nextKeys = checked
      ? [...selectedRowKeys, rowKey]
      : selectedRowKeys.filter((key) => key !== rowKey);

    handleChangeRow(nextKeys);
  }, [getRowKey, handleChangeRow, selectedRowKeys]);

  return {
    selectedKeySet,
    isAllSelected,
    isPartiallySelected,
    handleChangeRow,
    handleToggleAll,
    toggleRow,
  };
};
